import {
	chainCommands,
	deleteSelection,
	joinBackward,
	liftEmptyBlock,
	newlineInCode,
	selectAll,
	selectNodeBackward,
	splitBlock,
} from "prosemirror-commands";
import { liftListItem, sinkListItem } from "prosemirror-schema-list";
import { type EditorState, Selection, TextSelection, type Transaction } from "prosemirror-state";

import { platformIsApple } from "@moment/app/utils/platform/platformIsApple";
import { schema } from "@moment/prosemirror-schema";

import { CANVAS_BODY_ID } from "../../../views/constants";
import { createEmptyParagraphBeforeIfComputeCellSelected } from "./createEmptyParagraphBeforeIfComputeCellSelected";
import { createParagraphNear } from "./createParagraphNear";
import {
	indentListItem,
	splitListItem,
	unindentEmptyListItem,
	unindentListItem,
} from "./distinctListItems";
import { escapeCodeBlock } from "./escapeCodeBlock";
import { focusTitleOnArrowUp } from "./focusTitleOnArrowUp";
import { handleArrowDownInComputeCell } from "./handleArrowDownInComputeCell";
import { handleArrowLeftInComputeCell } from "./handleArrowLeftInComputeCell";
import { handleArrowRightInComputeCell } from "./handleArrowRightInComputeCell";
import { handleArrowUpInComputeCell } from "./handleArrowUpInComputeCell";
import { ignoreIfAllSelection } from "./ignoreIfAllSelection";
import { ignoreIfComputeCellSelected } from "./ignoreIfComputeCellSelected";
import { joinForward } from "./joinListItemsForward";
import { replaceDocWithEmptyParagraphIfAllSelection } from "./replaceDocWithEmptyParagraph";
import { replaceInvalidDeletes } from "./replaceInvalidDeletes";

const mac = platformIsApple();

const backspace = chainCommands(
	replaceDocWithEmptyParagraphIfAllSelection,
	replaceInvalidDeletes,
	// Ignore delete if the selection is in a `compute_cell`. We need to pop a confirmation
	// modal, which is fundamentally async.
	ignoreIfComputeCellSelected,
	deleteSelection,
	joinBackward,
	selectNodeBackward
);

const baseKeyMap = {
	// IMPORTANT: Mod-a should produce an `AllSelection`. If it doesn't you can't select a
	// `compute_cell` that appears first in the document. This is NOT the default behavior.
	"Mod-a": selectAll,

	"ArrowDown": handleArrowDownInComputeCell,
	"ArrowRight": handleArrowRightInComputeCell,
	"ArrowUp": chainCommands(handleArrowUpInComputeCell, focusTitleOnArrowUp),
	"ArrowLeft": handleArrowLeftInComputeCell,

	"Enter": chainCommands(
		replaceDocWithEmptyParagraphIfAllSelection,
		createEmptyParagraphBeforeIfComputeCellSelected,
		splitListItem(schema.nodes.list_item),
		splitListItem(schema.nodes.to_do_item),
		escapeCodeBlock,
		newlineInCode,
		createParagraphNear,
		unindentEmptyListItem,
		liftEmptyBlock,
		splitBlock
	),

	"Backspace": backspace,

	"Delete": chainCommands(
		replaceDocWithEmptyParagraphIfAllSelection,
		replaceInvalidDeletes,
		// Ignore delete if the selection is in a `compute_cell`. We need to pop a confirmation
		// modal, which is fundamentally async.
		ignoreIfComputeCellSelected,
		deleteSelection,
		joinForward
	),

	"Tab": chainCommands(
		ignoreIfAllSelection,
		indentListItem,
		sinkListItem(schema.nodes.list_item),
		// Returning true here will prevent the default <tab> behavior of blurring (un-focusing)
		// the editor.
		() => true
	),
	"Shift-Tab": chainCommands(
		ignoreIfAllSelection,
		unindentListItem,
		liftListItem(schema.nodes.list_item),
		// Returning true here will prevent the default <tab> behavior of blurring (un-focusing)
		// the editor.
		() => true
	),

	"Ctrl-Backspace": chainCommands(
		(state, dispatch) => {
			if (!(state.selection instanceof TextSelection) || !state.selection.empty) {
				return false;
			}

			if (!dispatch) {
				return true;
			}

			const { $cursor } = state.selection;

			const parentText = $cursor.parent.textBetween(0, $cursor.parentOffset - 1, " ", "x");
			const prevWhitespace = parentText.lastIndexOf(" ");
			if (prevWhitespace === -1) {
				dispatch(state.tr.delete($cursor.pos - $cursor.parentOffset, $cursor.pos));
				return true;
			}

			dispatch(
				state.tr.delete(
					$cursor.pos - $cursor.parentOffset + prevWhitespace + 1,
					$cursor.pos
				)
			);
			return true;
		},
		replaceDocWithEmptyParagraphIfAllSelection,
		replaceInvalidDeletes,
		// Ignore delete if the selection is in a `compute_cell`. We need to pop a confirmation
		// modal, which is fundamentally async.
		ignoreIfComputeCellSelected,
		deleteSelection,
		joinBackward,
		selectNodeBackward
	),

	"Mod-ArrowUp": (state: EditorState, dispatch: (tr: Transaction) => void) => {
		dispatch?.(state.tr.setSelection(Selection.atStart(state.doc)));
		document
			.getElementById(CANVAS_BODY_ID)
			?.scrollIntoView({ behavior: "instant", block: "start" });
		return true;
	},

	"Mod-Enter": (state: EditorState, dispatch: (tr: Transaction) => void) => {
		const { selection } = state;
		const blockRange = selection.$anchor.blockRange(selection.$head);

		const todos: number[] = [];
		let markCompleted = false;
		state.doc.nodesBetween(blockRange.start, blockRange.end, (node, pos) => {
			if (node.type !== schema.nodes["to_do_item"]) return true;

			todos.push(pos);
			if (node.attrs["status"] === "to do") {
				markCompleted = true;
			}
		});

		if (!dispatch) return !!todos.length;

		if (todos.length) {
			const tr = state.tr;
			for (const pos of todos) {
				tr.setNodeAttribute(pos, "status", markCompleted ? "completed" : "to do");
			}
			dispatch(tr);
			return true;
		}
		return false;
	},
};

export const keyMap = () => ({
	...baseKeyMap,
	...(mac && {
		"Cmd-Backspace": chainCommands((state, dispatch, view) => {
			if (!(state.selection instanceof TextSelection) || !state.selection.empty) {
				return false;
			}

			const { $cursor } = state.selection;
			let { node: dom } = view.domAtPos($cursor.pos);
			if (!(dom instanceof HTMLElement)) {
				dom = dom.parentElement;
			}

			if (!dispatch) {
				return true;
			}

			const coords = view.coordsAtPos($cursor.pos);
			// We ensure this is an HTMLElement above
			const { left } = (dom as HTMLElement).getBoundingClientRect();
			const { top } = coords;
			const { pos: deleteTo } = view.posAtCoords({ top, left });

			dispatch(state.tr.delete(deleteTo, $cursor.pos));
			return true;
		}, backspace),
	}),
});
